import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { RefreshCw, CreditCard, ExternalLink } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { API } from "../../App";

const SOURCE_LABELS = {
  connect_invoice_payment: "Payment link",
  portal_invoice_payment: "Customer portal",
  tech_onsite_payment: "Tech onsite",
  tap_to_pay_payment: "Tap to Pay",
};

const KIND_LABELS = { deposit: "Deposit", balance: "Balance", custom: "Partial" };

const statusBadge = (t) => {
  const ps = t.payment_status;
  if (ps === "paid") return <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30" data-testid="feed-status-paid">Paid</Badge>;
  if (t.status === "expired") return <Badge variant="outline" className="text-zinc-500">Expired</Badge>;
  if (ps === "pending" || ps === "unpaid" || t.status === "initiated") return <Badge className="bg-amber-500/15 text-amber-600 border-amber-500/30">Pending</Badge>;
  return <Badge variant="outline" className="text-zinc-500">{ps || t.status || "—"}</Badge>;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? "—" : d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

const money = (v) => `$${(Number(v) || 0).toFixed(2)}`;

const PaymentsFeedTab = ({ isDark, headers, onOpenInvoice }) => {
  const [loading, setLoading] = useState(true);
  const [txns, setTxns] = useState([]);
  const [summary, setSummary] = useState({});

  const fetchFeed = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/stripe/connect/payments-feed?limit=100`, { headers });
      setTxns(res.data?.transactions || []);
      setSummary(res.data?.summary || {});
    } catch { /* endpoint errors surface as empty feed */ }
    finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { fetchFeed(); }, [fetchFeed]);

  const cards = [
    { label: "Collected", value: money(summary.collected_total), cls: "text-emerald-500", testid: "feed-stat-collected" },
    { label: "Payments", value: summary.paid_count ?? 0, cls: "text-blue-500", testid: "feed-stat-count" },
    { label: "Pending Links", value: summary.pending_count ?? 0, cls: "text-amber-500", testid: "feed-stat-pending" },
    { label: "Tips", value: money(summary.tips_total), cls: "text-purple-500", testid: "feed-stat-tips" },
    { label: "Platform Fees", value: money(summary.fees_total), cls: "text-zinc-400", testid: "feed-stat-fees" },
  ];

  const cardCls = isDark ? "bg-zinc-900/50 border border-zinc-800" : "bg-white border border-gray-200 shadow-sm";

  return (
    <div className="space-y-4" data-testid="payments-feed-tab">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {cards.map((c) => (
          <div key={c.label} className={`rounded-xl px-3 py-2.5 ${cardCls}`} data-testid={c.testid}>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{c.label}</p>
            <p className={`text-base font-bold ${c.cls}`}>{c.value}</p>
          </div>
        ))}
      </div>

      <div className={`rounded-xl overflow-hidden ${cardCls}`}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <CreditCard className="w-4 h-4 text-[#635BFF]" />
            <h3 className="text-sm font-semibold text-foreground">Stripe Payments</h3>
            <span className="text-xs text-muted-foreground">last {txns.length} transactions</span>
          </div>
          <Button variant="ghost" size="sm" onClick={fetchFeed} className="gap-1.5 text-muted-foreground" data-testid="feed-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>

        {loading ? (
          <div className="py-12 text-center text-muted-foreground text-sm">Loading payments…</div>
        ) : txns.length === 0 ? (
          <div className="py-12 text-center text-muted-foreground text-sm" data-testid="feed-empty">
            No online payments yet. Create a payment link from any invoice to get started.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={`text-[11px] uppercase tracking-wider text-muted-foreground ${isDark ? "bg-zinc-800/40" : "bg-gray-50"}`}>
                  <th className="text-left px-4 py-2 font-medium">Date</th>
                  <th className="text-left px-3 py-2 font-medium">Customer</th>
                  <th className="text-left px-3 py-2 font-medium">Invoice</th>
                  <th className="text-left px-3 py-2 font-medium">Source</th>
                  <th className="text-right px-3 py-2 font-medium">Amount</th>
                  <th className="text-right px-3 py-2 font-medium">Tip</th>
                  <th className="text-right px-3 py-2 font-medium">Fee</th>
                  <th className="text-left px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {txns.map((t, i) => (
                  <tr
                    key={t.session_id || i}
                    className={`border-t border-border ${isDark ? "hover:bg-zinc-800/40" : "hover:bg-gray-50"}`}
                    data-testid={`feed-row-${i}`}
                  >
                    <td className="px-4 py-2.5 whitespace-nowrap text-muted-foreground">{fmtDate(t.created_at)}</td>
                    <td className="px-3 py-2.5 font-medium text-foreground max-w-[160px] truncate">{t.customer_name || "—"}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-muted-foreground">{t.invoice_number || "—"}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-muted-foreground">
                      {SOURCE_LABELS[t.payment_type] || t.payment_type}
                      {t.payment_kind && t.payment_kind !== "balance" && (
                        <span className="ml-1.5 text-[10px] uppercase text-amber-500">{KIND_LABELS[t.payment_kind] || t.payment_kind}</span>
                      )}
                      {t.technician_name && <span className="ml-1.5 text-[10px] text-muted-foreground">({t.technician_name})</span>}
                    </td>
                    <td className="px-3 py-2.5 text-right font-semibold text-foreground whitespace-nowrap">{money(t.invoice_amount ?? t.amount)}</td>
                    <td className="px-3 py-2.5 text-right text-muted-foreground whitespace-nowrap">{(t.tip_amount || 0) > 0 ? money(t.tip_amount) : "—"}</td>
                    <td className="px-3 py-2.5 text-right text-muted-foreground whitespace-nowrap">{(t.application_fee || 0) > 0 ? money(t.application_fee) : "—"}</td>
                    <td className="px-3 py-2.5">{statusBadge(t)}</td>
                    <td className="px-3 py-2.5 text-right">
                      {t.invoice_id && onOpenInvoice && (
                        <Button
                          variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-blue-500"
                          onClick={() => onOpenInvoice(t.invoice_id)}
                          title="Open invoice"
                          data-testid={`feed-open-invoice-${i}`}
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentsFeedTab;
